import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Icon,
  Panel,
  Field,
  Input,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
  type TagTone,
} from '@dayon/design-system';
import {
  RETIRED_RAIL_KINDS,
  type PaymentRailBrand,
  type PayoutProviderStatus,
} from '@dayon/contracts';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { RailBrandsDialog } from './RailBrandsDialog.tsx';
import { RailLimitsDialog } from './RailLimitsDialog.tsx';

/**
 * Payment management — the deposit/payout rail control room.
 *
 * Two GET endpoints back this screen: `/api/payments/methods`
 * (payment.payment_method plus a 30-day deposit rollup, and which brands offer
 * each rail) and `/api/payments/errors` (payment.payment_error_code plus a
 * 30-day count).
 *
 * THE WRITES, each behind platform.manage, each with a reason on the audit trail:
 *   POST /api/payments/methods/:id/status              a rail's status
 *   POST /api/payments/methods/:id/fee-bearer          who pays its payout fee
 *   POST /api/payments/methods/:id/brands/:brandId     a brand offering it, or not (RailBrandsDialog.tsx)
 *   POST /api/payments/methods/:id/limits              its per-transaction limits (RailLimitsDialog.tsx)
 *
 * And `/api/payouts/provider`, for a reader who may switch a rail's brands: which payout driver is on,
 * which decides what offering the StarPay rail means for a brand's players (RailBrandsDialog.tsx).
 *
 * Snake_case field names below are the SQL SELECT list, not a style slip.
 */
interface RailRow {
  id: string;
  name: string;
  /** EWALLET · BANK_TRANSFER. CARD and CRYPTO appear only on a retired rail. */
  kind: string;
  provider: string;
  /** LIVE · DEGRADED · DISABLED */
  status: string;
  min_amount: string;
  max_amount: string;
  fee_basis_points: string;
  fee_flat: string;
  supports_deposit: boolean;
  supports_withdrawal: boolean;
  deposit_count: string;
  /** Null when the rail took no deposits in the window — never a zero. */
  success_bp: string | null;
  deposit_value: string;
  withdrawal_count: string;
  /** Requests still at a checker or on risk hold — on the road, not off it. */
  withdrawal_pending: string;
  /**
   * Released ÷ DECIDED, null when nothing has been decided yet.
   *
   * Not released ÷ every request: one can sit awaiting a checker for days, and
   * counting that against the RAIL would make the figure fall whenever the
   * desk is behind and rise when somebody clears the queue — the desk's
   * backlog wearing a payment provider's name.
   */
  payout_success_bp: string | null;
  payout_value: string;
  /** PLAYER | OPERATOR — who pays this rail's PAYOUT fee. Deposits are the provider's model. */
  fee_borne_by: string;
  fee_bearer_reason: string | null;
  fee_bearer_changed_at: string | null;
  fee_bearer_changed_by: string | null;
  /** Every active brand in the reader's scope, and whether it offers this rail to its players. */
  brands: PaymentRailBrand[];
}

interface ErrorRow {
  code: string;
  provider: string;
  meaning: string;
  retry_safe: boolean;
  seen_30d: string;
}

/** A provider grouping derived from the rails. There is no provider entity. */
interface ProviderRollup {
  provider: string;
  rails: RailRow[];
}

const KIND_LABEL: Record<string, string> = {
  EWALLET: 'E-wallet',
  BANK_TRANSFER: 'Bank transfer',
  CARD: 'Card',
  CRYPTO: 'Crypto',
};
const KIND_ORDER = ['EWALLET', 'BANK_TRANSFER', 'CARD', 'CRYPTO'];

const STATUS_LABEL: Record<string, string> = {
  LIVE: 'Live',
  DEGRADED: 'Degraded',
  DISABLED: 'Disabled',
};

/**
 * "Degraded is set by monitoring, Disabled by an admin" — two different writers
 * on one column, so the three states get three distinct treatments rather than
 * collapsing into on/off. Live is filled, Degraded is outlined (still an accent:
 * it wants attention), Disabled is quiet.
 */
const STATUS_TONE: Record<string, TagTone> = {
  LIVE: 'accent',
  DEGRADED: 'outline',
  DISABLED: 'quiet',
};

type RailStatus = 'LIVE' | 'DEGRADED' | 'DISABLED';
type StatusFilter = 'ALL' | RailStatus;

/**
 * "Status chips are mutually exclusive" — so this is a radio group, and the keys
 * are typed here rather than inline so the chip row and `setFilter` agree.
 */
const STATUS_FILTERS: readonly { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'LIVE', label: 'Live' },
  { key: 'DEGRADED', label: 'Degraded' },
  { key: 'DISABLED', label: 'Disabled' },
];

/**
 * The operator-side cost, assembled from the two columns that hold it.
 * "Fees shown are what the provider charges the operator, never shown to the
 * player" — which is why this lives on an admin table and nowhere else.
 */
function formatFee(basisPoints: number, flat: bigint): string {
  if (basisPoints > 0 && flat > 0n) return `${formatRate(basisPoints)} + ${formatPesos(flat)}`;
  if (basisPoints > 0) return formatRate(basisPoints);
  if (flat > 0n) return `${formatPesos(flat)} flat`;
  return 'No fee';
}

/**
 * Declared as a factory rather than a const because the last column dispatches a
 * row action, and a column array that closes over a handler is the only way to
 * keep the row action out of the table component.
 */
function railColumns(
  /** Null for a role that may read this screen and not change a rail. */
  onToggle: ((rail: RailRow) => void) | null,
  onMoveFee: ((rail: RailRow) => void) | null,
  onBrands: ((rail: RailRow) => void) | null,
  onLimits: ((rail: RailRow) => void) | null,
): Column<RailRow>[] {
  return [
    {
      key: 'rail',
      header: 'Rail',
      render: (r) => (
        <>
          {r.name}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {[r.supports_deposit && 'Deposits', r.supports_withdrawal && 'Payouts']
              .filter(Boolean)
              .join(' · ') || 'Neither direction enabled'}
          </div>
        </>
      ),
    },
    {
      key: 'kind',
      header: 'Type',
      secondary: true,
      render: (r) => <span className="text-muted">{KIND_LABEL[r.kind] ?? r.kind}</span>,
    },
    {
      key: 'provider',
      header: 'Provider',
      render: (r) => <span className="text-muted">{r.provider}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Tag tone={STATUS_TONE[r.status] ?? 'neutral'}>{STATUS_LABEL[r.status] ?? r.status}</Tag>
      ),
    },
    {
      /* WHICH BRANDS OFFER IT, by name: a brand's players see a rail on their deposit and withdraw
         screens only when their brand offers it, and the Brands control beside the row switches
         one. The brands are the reader's own — a brand-scoped admin sees their brands only. */
      key: 'brands',
      header: 'Brands',
      render: (r) => {
        const offering = r.brands.filter((b) => b.enabled);
        return (
          <>
            {offering.length === 0 ? (
              <span className="text-subtle">offered to no brand</span>
            ) : (
              offering.map((b) => b.brandName).join(' · ')
            )}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatCount(offering.length)} of {formatCount(r.brands.length)}{' '}
              {r.brands.length === 1 ? 'brand' : 'brands'}
            </div>
          </>
        );
      },
    },
    {
      key: 'min',
      header: 'Min',
      secondary: true,
      numeric: true,
      render: (r) => <span className="tabular">{formatPesos(BigInt(r.min_amount))}</span>,
    },
    {
      key: 'max',
      header: 'Max',
      numeric: true,
      render: (r) => <span className="tabular">{formatPesos(BigInt(r.max_amount))}</span>,
    },
    {
      key: 'fee',
      header: 'Fee',
      numeric: true,
      render: (r) => (
        <span className="tabular">
          {formatFee(Number(r.fee_basis_points), BigInt(r.fee_flat))}
        </span>
      ),
    },
    {
      key: 'success',
      header: 'Success 30d',
      numeric: true,
      // The endpoint measures SETTLED ÷ total deposits over 30 days. The design
      // asks for 24h; the window is stated in the header rather than relabelled,
      // because a 30-day number wearing a 24h label is the worst of both.
      render: (r) =>
        r.success_bp === null ? (
          <span className="text-subtle">— no attempts</span>
        ) : (
          <>
            <span className="tabular">{formatRate(Number(r.success_bp))}</span>
            {/* The denominator matters: a degraded rail can read 100% on 11
                attempts, and an operator must see that before trusting it. */}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatCount(Number(r.deposit_count))} attempts
            </div>
          </>
        ),
    },
    {
      key: 'bearer',
      header: 'Payout fee',
      secondary: true,
      render: (r) => {
        if (!r.supports_withdrawal) {
          return <span className="text-subtle">— deposits only</span>;
        }
        const player = r.fee_borne_by === 'PLAYER';
        return (
          <>
            <Tag tone={player ? 'accent' : 'quiet'}>{player ? 'Player pays' : 'Operator pays'}</Tag>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {player
                ? 'deducted from what the player receives'
                : 'absorbed — the player receives the full amount'}
            </div>
            {/* Who last moved it and why, on the row rather than in a log
                somebody has to go and find. A rail that started charging
                players is the first thing asked about when they notice. */}
            {r.fee_bearer_changed_by ? (
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                {r.fee_bearer_changed_by}: {r.fee_bearer_reason}
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: 'payout',
      header: 'Payouts 30d',
      numeric: true,
      secondary: true,
      /*
       * THE HALF THIS SCREEN SAID COULD NOT BE SHOWN. The notice read "Nothing
       * on the platform reports withdrawal outcomes per rail"; every
       * ingredient was in `payment.withdrawal` all along.
       */
      render: (r) => {
        if (!r.supports_withdrawal) {
          return <span className="text-subtle">— deposits only</span>;
        }
        const waiting = Number(r.withdrawal_pending);
        if (r.payout_success_bp === null) {
          /* NOT "0%". Nothing decided is not the same fact as everything
             refused, and a rail with a queue still waiting has told you
             nothing about itself yet. */
          return (
            <span className="text-subtle">
              {waiting > 0 ? `— ${formatCount(waiting)} still waiting` : '— none requested'}
            </span>
          );
        }
        return (
          <>
            <span className="tabular">{formatRate(Number(r.payout_success_bp))}</span>
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {formatCount(Number(r.withdrawal_count) - waiting)} decided
              {waiting > 0 ? ` · ${formatCount(waiting)} still waiting, not counted` : ''}
            </div>
          </>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (r) => {
        const verb = r.status === 'DISABLED' ? 'Enable' : 'Disable';
        /*
         * A CARD OR CRYPTO RAIL CANNOT BE ENABLED, AND THE ROW SAYS SO.
         *
         * The licence bars both for funding and withdrawal, and sql/900 refuses
         * the UPDATE — so an Enable button here would be a control that always
         * fails. The reason is printed where the button was rather than left for
         * somebody to discover by pressing it, which is the same call the payout
         * fee button below makes for the same reason.
         */
        const retired = RETIRED_RAIL_KINDS.has(r.kind);
        return (
          <div className="row-actions">
            {/* ABSENT rather than drawn dim for a role without the key: a greyed
                control still tells somebody the power exists and invites them to
                go looking for it. The notice under the table names the key. */}
            {retired ? (
              <span className="text-subtle" style={{ fontSize: '11px' }}>
                Cannot be enabled — kept only for past deposits and payouts
              </span>
            ) : onToggle ? (
              <Button small onClick={() => onToggle(r)} aria-label={`${verb} ${r.name}`}>
                {verb}
              </Button>
            ) : null}
            {/* ONLY WHERE IT GOVERNS SOMETHING. A deposit-only rail has no
                payout fee for anybody to bear, and the route refuses it by
                name — so the button is absent rather than offered and then
                refused. A role without the key gets no button either, and the
                notice under the table names the key. */}
            {onMoveFee && r.supports_withdrawal && !retired ? (
              <Button
                small
                variant="secondary"
                onClick={() => onMoveFee(r)}
                aria-label={`Change who pays the payout fee on ${r.name}`}
              >
                Payout fee
              </Button>
            ) : null}
            {/* The brands that offer the rail, and its limits — the two that production once needed
                SQL for. Absent for a role without the key, and for a rail the licence bars, which
                is offered to nobody and takes nothing. */}
            {onBrands && !retired ? (
              <Button
                small
                variant="secondary"
                onClick={() => onBrands(r)}
                aria-label={`Brands for ${r.name}`}
              >
                Brands
              </Button>
            ) : null}
            {onLimits && !retired ? (
              <Button
                small
                variant="secondary"
                onClick={() => onLimits(r)}
                aria-label={`Limits for ${r.name}`}
              >
                Limits
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];
}

/**
 * "Error codes carry explicit retry guidance — this is the operator's runbook,
 * not just a count." `retry_safe` is a real boolean on payment_error_code, so it
 * gets its own column instead of being left to whether the prose happens to say
 * it: GC-402 and NB-118 carry no guidance in their `meaning` text at all.
 */
const errorColumns: Column<ErrorRow>[] = [
  {
    key: 'code',
    header: 'Code',
    render: (e) => <Tag tone="accent">{e.code}</Tag>,
  },
  {
    key: 'provider',
    header: 'Provider',
    secondary: true,
    render: (e) => <span className="text-muted">{e.provider}</span>,
  },
  {
    key: 'seen',
    header: 'Seen 30d',
    numeric: true,
    render: (e) => <span className="tabular">{formatCount(Number(e.seen_30d))}</span>,
  },
  {
    key: 'retry',
    header: 'Retry',
    render: (e) => (
      <Tag tone={e.retry_safe ? 'neutral' : 'outline'}>
        {e.retry_safe ? 'Retry safe' : 'Do not retry'}
      </Tag>
    ),
  },
  {
    key: 'meaning',
    header: 'Meaning',
    render: (e) => <span className="text-muted">{e.meaning}</span>,
  },
];

/**
 * The provider drill-down, grouped out of the rails.
 *
 * There is deliberately NO provider "Status" column: "rail status is
 * operator-controlled and independent of provider health", so deriving a
 * provider's health from its rails' states would invent exactly the signal the
 * rule says does not follow.
 */
const providerColumns: Column<ProviderRollup>[] = [
  { key: 'provider', header: 'Provider', render: (p) => p.provider },
  {
    key: 'rails',
    header: 'Rails',
    render: (p) => (
      <>
        {p.rails.map((r) => r.name).join(' · ')}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {p.rails.length} rail{p.rails.length === 1 ? '' : 's'}
        </div>
      </>
    ),
  },
  {
    key: 'states',
    header: 'Rail states',
    render: (p) => {
      const counts = new Map<string, number>();
      for (const r of p.rails) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
      return (
        <span className="row">
          {[...counts].map(([status, n]) => (
            <Tag key={status} tone={STATUS_TONE[status] ?? 'neutral'}>
              {n} {STATUS_LABEL[status] ?? status}
            </Tag>
          ))}
        </span>
      );
    },
  },
];

export function Payments() {
  const { brandId, brands, session, allowed } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadLedger = allowed('transactions.view');
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  /* Bumped after the one write this screen has, so the rails re-read and the
     row shows who moved the fee and why without a manual refresh. */
  const [reload, setReload] = useState(0);

  const [rails, setRails] = useState<RailRow[]>([]);
  const [errors, setErrors] = useState<ErrorRow[]>([]);
  const [railsLoading, setRailsLoading] = useState(true);
  const [errorsLoading, setErrorsLoading] = useState(true);
  /*
   * Whether each read FAILED, as distinct from answering with nothing — and one
   * flag per endpoint, never one for the screen.
   *
   * The catch opened the error modal and stopped there, so behind it the rails
   * table fell through to "payment.payment_method holds no rows" and the KPI
   * band to "0 of 0" rails live and ₱0 settled. Every one of those is a
   * measurement, and none of them had been measured: the request never landed.
   * An operator who dismisses the modal reads a platform that takes no
   * deposits. "No rail is configured" and "we could not ask" are different
   * facts and only one is safe to act on.
   *
   * Two flags because the two endpoints fail apart: /api/payments/errors can be
   * down while the rails answer perfectly, and a single flag would then brand a
   * healthy rail table unreadable — or, worse, let a dead rail read borrow the
   * error list's success.
   */
  const [railsFailed, setRailsFailed] = useState(false);
  const [errorsFailed, setErrorsFailed] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('ALL');
  const [toggling, setToggling] = useState<RailRow | null>(null);
  /** The rail whose payout fee is being moved, and the form beside it. */
  const [movingFee, setMovingFee] = useState<RailRow | null>(null);
  const [feeBearer, setFeeBearer] = useState<'PLAYER' | 'OPERATOR'>('OPERATOR');
  const [feeReason, setFeeReason] = useState('');
  const [feeReasonError, setFeeReasonError] = useState<string>();
  const [movingFeeBusy, setMovingFeeBusy] = useState(false);
  /** Where the rail being toggled is going, and why. */
  const [statusTo, setStatusTo] = useState<RailStatus>('DISABLED');
  const [statusReason, setStatusReason] = useState('');
  const [statusReasonError, setStatusReasonError] = useState<string>();
  const [togglingBusy, setTogglingBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [providersOpen, setProvidersOpen] = useState(false);
  /** The rail whose brands, or whose limits, are being set (RailBrandsDialog, RailLimitsDialog). */
  const [brandsFor, setBrandsFor] = useState<RailRow | null>(null);
  const [limitsFor, setLimitsFor] = useState<RailRow | null>(null);

  // brandId rides along for consistency with every other module, but neither
  // handler calls brandScope() — see the banner below, which says so out loud
  // rather than letting the brand picker look like it narrowed the list.
  const suffix = useMemo(() => {
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    return params.toString() ? `?${params}` : '';
  }, [brandId]);

  /* The rails. Its own request, its own outcome — the two reads were a single
     Promise.all with one catch, which could only ever report that "something"
     failed. */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadLedger) {
      setRailsLoading(false);
      return;
    }
    let cancelled = false;
    setRailsLoading(true);

    api
      .get<{ items: RailRow[] }>(`/api/payments/methods${suffix}`)
      .then((d) => {
        if (cancelled) return;
        setRails(d.items);
        setRailsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared, not left standing: a stale rail list under a failure notice
        // would still be driving the KPI band, the chip counts and the Limits
        // card, all of which read as current.
        setRails([]);
        setRailsFailed(true);
        showError({
          title: 'Could not load the payment rails',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The payment rails could not be fetched. Try again shortly.',
        });
      })
      .finally(() => !cancelled && setRailsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadLedger, suffix, showError, reload]);

  /* The provider error codes — the operator's runbook, and independent of the
     rails in both directions. */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadLedger) {
      setErrorsLoading(false);
      return;
    }
    let cancelled = false;
    setErrorsLoading(true);

    api
      .get<{ items: ErrorRow[] }>(`/api/payments/errors${suffix}`)
      .then((d) => {
        if (cancelled) return;
        setErrors(d.items);
        setErrorsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setErrors([]);
        setErrorsFailed(true);
        showError({
          title: 'Could not load the provider error codes',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The provider error codes could not be fetched. Try again shortly.',
        });
      })
      .finally(() => !cancelled && setErrorsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadLedger, suffix, showError]);

  const counts = useMemo(() => {
    const by = { ALL: rails.length, LIVE: 0, DEGRADED: 0, DISABLED: 0 };
    for (const r of rails) {
      if (r.status === 'LIVE') by.LIVE += 1;
      else if (r.status === 'DEGRADED') by.DEGRADED += 1;
      else if (r.status === 'DISABLED') by.DISABLED += 1;
    }
    return by;
  }, [rails]);

  /** Volume-weighted, so one rail with eleven attempts cannot move the board. */
  const deposits = useMemo(() => {
    let attempts = 0;
    let weighted = 0;
    let settledValue = 0n;
    for (const r of rails) {
      const n = Number(r.deposit_count);
      attempts += n;
      weighted += n * Number(r.success_bp ?? 0);
      settledValue += BigInt(r.deposit_value);
    }
    return {
      attempts,
      successBp: attempts === 0 ? null : Math.round(weighted / attempts),
      settledValue,
    };
  }, [rails]);

  /**
   * The Limits card, derived from the rails themselves rather than stated.
   * "Limits cascade — a rail cannot exceed the platform ceiling" — the per-rail
   * range is the only tier of that cascade the schema actually holds, so this
   * card shows the observed band per rail type and names the missing tiers.
   */
  const limitBands = useMemo(() => {
    const byKind = new Map<string, { min: bigint; max: bigint; rails: number }>();
    for (const r of rails) {
      const min = BigInt(r.min_amount);
      const max = BigInt(r.max_amount);
      const cur = byKind.get(r.kind);
      byKind.set(
        r.kind,
        cur
          ? { min: min < cur.min ? min : cur.min, max: max > cur.max ? max : cur.max, rails: cur.rails + 1 }
          : { min, max, rails: 1 },
      );
    }
    return [...byKind.entries()].toSorted(
      (a, b) => KIND_ORDER.indexOf(a[0]) - KIND_ORDER.indexOf(b[0]),
    );
  }, [rails]);

  const providers = useMemo<ProviderRollup[]>(() => {
    const byProvider = new Map<string, RailRow[]>();
    for (const r of rails) {
      const list = byProvider.get(r.provider);
      if (list) list.push(r);
      else byProvider.set(r.provider, [r]);
    }
    return [...byProvider.entries()]
      .map(([provider, list]) => ({ provider, rails: list }))
      .toSorted((a, b) => a.provider.localeCompare(b.provider));
  }, [rails]);

  const visible = useMemo(
    () => (filter === 'ALL' ? rails : rails.filter((r) => r.status === filter)),
    [rails, filter],
  );

  /**
   * The key that may move a fee onto players.
   *
   * `platform.manage` bears a second factor and is the key for configuring how
   * the platform behaves. Deciding that players pay for their own payouts is
   * that, not an operational act like working a queue — so an operator who can
   * read this screen does not necessarily get the button, and the notice under
   * the rails table says which key is missing rather than leaving a gap.
   */
  const canMoveFee = allowed('platform.manage');
  /** The same key: both are configuring how the platform behaves. */
  const canMoveStatus = allowed('platform.manage');
  /** And the brands that offer a rail and its limits: the fee edit's key, for the same reason. */
  const canSetRail = allowed('platform.manage');

  /**
   * Which payout driver is on, for the Brands dialog: with no payout provider configured the StarPay
   * rail is left out of players' withdraw screens, so offering it shows them nothing until one is,
   * and the dialog says so. Read once, and only for a reader who can open that dialog. A supporting
   * read, like the funds screen's: a failure opens no dialog of its own, and the driver stays not
   * known (null), so the Brands dialog keeps the sentence it had — and the route's own sentence,
   * after the press, is said by the API, which knows its driver.
   */
  const [payoutDriver, setPayoutDriver] = useState<PayoutProviderStatus['driverName'] | null>(null);
  useEffect(() => {
    if (!canReadLedger || !canSetRail) return;
    let cancelled = false;
    api
      .get<PayoutProviderStatus>('/api/payouts/provider')
      .then((status) => {
        if (!cancelled) setPayoutDriver(status.driverName);
      })
      .catch(() => {
        /* Not known: see above. */
      });
    return () => {
      cancelled = true;
    };
  }, [canReadLedger, canSetRail]);

  const moveFee = async () => {
    if (!movingFee) return;
    const reason = feeReason.trim();
    if (reason.length < 10) {
      setFeeReasonError('Say why this is changing — at least 10 characters.');
      return;
    }
    setFeeReasonError(undefined);
    setMovingFeeBusy(true);
    try {
      const result = await api.post<{ name: string; feeBorneBy: string; effect: string }>(
        `/api/payments/methods/${movingFee.id}/fee-bearer`,
        { bearer: feeBearer, reason },
      );
      toast(result.effect);
      setMovingFee(null);
      setFeeReason('');
      setReload((n: number) => n + 1);
    } catch (error: unknown) {
      showError({
        title: 'The payout fee was not moved',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed on this rail.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setMovingFeeBusy(false);
    }
  };
  /* Opened pointing at the OPPOSITE of where it is: the button says Enable or
     Disable, so landing on anything else would make the dropdown disagree with
     the button that opened it. The form is cleared each time — a reason typed
     for one rail must never be submitted against another. */
  const openToggle = useCallback((rail: RailRow) => {
    setStatusTo(rail.status === 'DISABLED' ? 'LIVE' : 'DISABLED');
    setStatusReason('');
    setStatusReasonError(undefined);
    setToggling(rail);
  }, []);

  const columns = useMemo(
    () =>
      railColumns(
        canMoveStatus ? openToggle : null,
        canMoveFee ? setMovingFee : null,
        canSetRail ? setBrandsFor : null,
        canSetRail ? setLimitsFor : null,
      ),
    [canMoveFee, canMoveStatus, canSetRail, openToggle],
  );

  /** How many rails players could still reach if this one were disabled. */
  const liveAfterDisabling = rails.filter(
    (r) => r.status !== 'DISABLED' && r.id !== toggling?.id,
  ).length;

  const applyStatus = async () => {
    if (!toggling) return;
    const reason = statusReason.trim();
    if (reason.length < 10) {
      setStatusReasonError('Say why this rail is moving — at least 10 characters.');
      return;
    }
    setStatusReasonError(undefined);
    setTogglingBusy(true);
    try {
      const result = await api.post<{
        name: string;
        status: string;
        railsLeftForDeposits: string;
        railsLeftForWithdrawals: string;
      }>(`/api/payments/methods/${toggling.id}/status`, { status: statusTo, reason });
      toast(
        `${result.name} is now ${result.status.toLowerCase()} — ` +
          `${result.railsLeftForDeposits} rails left for deposits, ` +
          `${result.railsLeftForWithdrawals} for payouts.`,
      );
      setToggling(null);
      setReload((n: number) => n + 1);
    } catch (error: unknown) {
      showError({
        title: 'The rail was not moved',
        message:
          error instanceof ApiRequestError ? error.message : 'Nothing was changed on this rail.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setTogglingBusy(false);
    }
  };

  /**
   * The design gates the rail toggle on `payments.manage`. That permission does
   * not exist in @dayon/rbac; the real gate on both endpoints and on the sidebar
   * entry is `transactions.view`, which the role matrix grants to Super Admin and
   * Finance Lead — exactly the two roles the design names. So the gate is honest
   * even though the key differs, and the whole screen is gated, not just the
   * button: without it the API returns 403 and there is nothing to render.
   */
  if (!canReadLedger) {
    return (
      <EmptyState icon="lock-key" title="Payment management">
        This module needs the transactions.view capability — the platform's gate for changing anything
        that moves money. Your role, {session?.role ?? 'unknown'}, does not hold it.
      </EmptyState>
    );
  }

  const degradedNames = rails.filter((r) => r.status === 'DEGRADED').map((r) => r.name);
  const brandName = brands.find((b) => b.id === brandId)?.name;
  const toggleShareBp =
    toggling && deposits.settledValue > 0n
      ? Number((BigInt(toggling.deposit_value) * 10000n) / deposits.settledValue)
      : null;

  return (
    <>
      {/*
        WHY A SENTENCE NOW SITS ABOVE THE RAIL COUNT.

        This screen used to open with a name and a count, and nothing else. An
        operator arriving on it — new to the desk, or sent here because players
        are complaining that a deposit will not go through — was given four
        figures, a filter row and a nine-column table, and had to work out the
        screen's job from the figures. The explanation existed all along, in the
        module comment at the top of this file, where only somebody editing the
        source would ever read it. It was always written for the operator; it
        was just in the wrong place.

        THE HEADING KEEPS "Payment management" THOUGH THE SIDEBAR SAYS "Payment
        methods", and that disagreement is deliberate for now rather than
        unnoticed. The capability refusal just above this return uses these same
        two words, so a Finance Lead who may read this screen and an Auditor who
        may not are shown one name for one screen; and the browser walk that covers
        this module finds the page by looking that heading up. Settling on one
        of the two names is worth doing — it is just a change that has to move
        the sidebar, the refusal and that walk together, which is more than a
        header swap.
      */}
      <ScreenHeader
        title="Payment management"
        purpose="The rails players use to put money in and take it out: finance watches which are working and takes a troubled one off the list."
        /*
          The caption moves across exactly as it was, and it is tri-state on
          purpose.

          'Loading…' means the answer has not arrived yet. The middle state
          means the request FAILED, so the two counts that would have stood here
          were never taken — which is why it says no count is shown instead of
          showing a zero: "this platform has no payment rails at all" is a
          sentence somebody acts on, and "nobody managed to ask" is not. Only
          the third state is a measurement. The long note beside `railsFailed`
          further up exists for precisely this distinction, and the four tiles,
          the filter badges, the Limits card and the table's empty state all
          lean on the same flag for the same reason.

          The per-rail limits clause stays inside the third state, where it
          qualifies a number that was actually read — appending it to the
          failure state would attach a confident aside to a line whose whole job
          is to admit it knows nothing.
        */
        meta={
          railsLoading && rails.length === 0
            ? 'Loading…'
            : railsFailed
              ? 'the rail list could not be read — no count is shown'
              : `${rails.length} rails · ${providers.length} providers · limits are per-rail only`
        }
        /*
          Both screen-level buttons move into `actions` unchanged, and neither
          is gated, which is worth saying because this console normally hides a
          control an operator may not use. Providers only regroups rails already
          on the screen, so it reveals nothing the table does not. Add rail
          opens a dialog whose entire content is the admission that creating a
          rail is not built anywhere on the platform — hiding that would leave
          somebody hunting for a control that does not exist, which is the more
          expensive outcome.
        */
        actions={
          <>
            <Button onClick={() => setProvidersOpen(true)}>
              <Icon name="buildings" />
              Providers
            </Button>
            <Button variant="primary" onClick={() => setAddOpen(true)}>
              <Icon name="plus" />
              Add rail
            </Button>
          </>
        }
      />

      {/* Rule 1 of the module, stated where the toggle lives rather than in a
          tooltip: the two non-live states have different writers. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Rail status has two writers. <strong>Degraded</strong> is set by monitoring;{' '}
          <strong>Disabled</strong> is set by an admin. Disabling blocks new attempts only —
          in-flight deposits and payouts are left to settle. Fees below are what the provider
          charges the operator and are never shown to a player.
        </div>
      </div>

      {brandId && (
        <div className="notice notice-quiet">
          <Icon name="warning-circle" />
          <div>
            Brand scope <strong>{brandName ?? brandId}</strong> does not narrow this list.{' '}
            This list is platform-wide and is not narrowed by brand. Which brands offer each rail
            is its <strong>Brands</strong> column. The tighter limits a brand can set for itself
            are recorded but cannot be read from this screen yet — so the second tier of the limit
            cascade is invisible here.
          </div>
        </div>
      )}

      {railsLoading && rails.length === 0 ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : railsFailed ? (
        /* Every figure in this band is computed from the rail list, and the
           rail list is what did not arrive. Rendered as measurements they would
           read "0 of 0 rails live", "₱0 settled" and "no deposit attempts in
           the window" — three confident zeroes about a platform nobody just
           asked. An em dash is the only honest value here. */
        <div className="stat-grid">
          <Stat label="Deposit success 30d" value="—" delta="the rail read failed — nothing measured" />
          <Stat label="Settled deposits 30d" value="—" delta="the rail read failed — nothing measured" />
          <Stat label="Rails live" value="—" delta="the rail read failed — nothing counted" />
          <Stat label="Rails degraded" value="—" delta="the rail read failed — nothing counted" />
        </div>
      ) : (
        <div className="stat-grid">
          <Stat
            label="Deposit success 30d"
            value={deposits.successBp === null ? '—' : formatRate(deposits.successBp)}
            delta={
              deposits.attempts === 0
                ? 'no deposit attempts in the window'
                : `${formatCount(deposits.attempts)} attempts · volume-weighted across rails`
            }
          />
          <Stat
            label="Settled deposits 30d"
            value={formatPesosCompact(deposits.settledValue)}
            delta="settled value only — pending and failed are excluded"
          />
          <Stat
            label="Rails live"
            value={`${counts.LIVE} of ${rails.length}`}
            delta={`${counts.DISABLED} disabled by an admin`}
          />
          <Stat
            label="Rails degraded"
            value={counts.DEGRADED}
            deltaUp={counts.DEGRADED > 0}
            delta={
              degradedNames.length > 0
                ? `${degradedNames.join(', ')} · flagged by monitoring`
                : 'no rail flagged by monitoring'
            }
          />
        </div>
      )}

      <Panel>
        <div className="panel-head">
          <h3 className="panel-title">Rails</h3>
          <div className="header-spacer">
            <Chips
              label="Filter rails by status"
              active={filter}
              onChange={setFilter}
              // A badge is a count. After a failed read there is nothing to
              // count, so the badges come off rather than all reading zero.
              options={
                railsFailed
                  ? STATUS_FILTERS.map((f) => ({ key: f.key, label: f.label }))
                  : STATUS_FILTERS.map((f) => ({ key: f.key, label: f.label, count: counts[f.key] }))
              }
            />
          </div>
        </div>

        {railsLoading && rails.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Payment rails with type, provider, operator-set status, per-transaction limits, operator-side fee and measured 30-day deposit success"
            columns={columns}
            rows={visible}
            rowKey={(r) => r.id}
            empty={
              <EmptyState
                icon={railsFailed ? 'warning-circle' : 'credit-card'}
                title={railsFailed ? 'The rails could not be read' : 'No rail in this state'}
              >
                {/* The failure branch is first and never says "real zero": an
                    empty table under a failed read was never filled.

                    THE OTHER TWO BRANCHES USED TO NAME THE TABLE. They read
                    "payment.payment_method holds no rows" and "read live from
                    payment.payment_method", and an operator looking at an empty
                    rail list can do nothing with that name: it is not something
                    they can open, ask anybody for, or quote to support. The
                    CLAIM each one made is the valuable part and is kept word for
                    word in meaning — one says the list was read and is
                    genuinely empty, the other says the filter found nothing in a
                    list that did arrive — because "there is no rail" and "we
                    could not ask" are different facts and only one of them is
                    safe to act on. The table is `payment.payment_method`, here
                    in the comment where whoever reads this source needs it.

                    AND THE FAILURE BRANCH USED TO NAME THE ENDPOINT. It opened
                    "/api/payments/methods did not answer", which is this screen
                    describing its own plumbing to somebody who cannot act on
                    it: an operator cannot open that address and cannot quote it
                    to anybody who will, and "the rail list could not be read"
                    tells them the one thing that matters — that nothing here
                    was measured — without it. The read that failed is the GET
                    of `/api/payments/methods` recorded at the top of this
                    file. */}
                {railsFailed
                  ? 'This table is empty because the rail list could not be read — not because no rail is configured. Nothing here says what can take a deposit right now. Try again shortly.'
                  : filter === 'ALL'
                    ? 'No payment rail is recorded at all. Nothing was filtered out — there is genuinely nothing set up to take a deposit or pay a withdrawal.'
                    : `No rail is currently ${STATUS_LABEL[filter]?.toLowerCase() ?? filter.toLowerCase()}. This is a real zero, not a failed read: the rail list arrived just now and no rail in it is in that state.`}
              </EmptyState>
            }
          />
        )}

        {/* THE KEY THAT IS MISSING, SAID WHERE THE BUTTONS WOULD BE. A role without it reads the
            rails with no Status, Payout fee, Brands or Limits control on any row; without this
            sentence the gap reads as a screen with nothing to change. */}
        {canSetRail ? null : (
          <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="lock-key" />
            <div>
              Changing a rail needs <strong>Configure brands, parameters &amp; announcements</strong>{' '}
              (<code>platform.manage</code>) — its status, who pays its payout
              fee, the brands that offer it and its limits — which {session?.role ?? 'your role'} does
              not hold, so those controls are not on the rows above.
            </div>
          </div>
        )}
      </Panel>

      <div className="halves">
        <Panel title="Limits">
          {limitBands.length === 0 ? (
            <EmptyState icon="ruler" title="No limits to show">
              {railsFailed
                ? 'Limits are read off the rails, and the rail list could not be read. This is not a statement that no rail is configured — nothing was measured.'
                : 'Limits are read off the rails, and no rail is configured.'}
            </EmptyState>
          ) : (
            <>
              <dl className="stack" style={{ margin: 0 }}>
                {limitBands.map(([kind, band]) => (
                  <div className="def-row" key={kind}>
                    <dt>
                      {KIND_LABEL[kind] ?? kind} per transaction
                      <span className="text-subtle">
                        {' '}
                        · {band.rails} rail{band.rails === 1 ? '' : 's'}
                      </span>
                    </dt>
                    <dd>
                      {formatPesos(band.min)} – {formatPesos(band.max)}
                    </dd>
                  </div>
                ))}
              </dl>
              {/* Rule 7. The design's Limits card also lists daily ceilings and a
                  KYC-scoped withdrawal ceiling. Rendering those as figures would
                  mean inventing them, so they are named as missing instead. */}
              <div className="notice notice-quiet" style={{ marginTop: 'var(--space-4)' }}>
                <Icon name="info" />
                <div>
                  These are the observed per-transaction bands across the rails above — the only
                  level of limits the platform keeps. The daily deposit ceiling, the minimum
                  withdrawal, the KYC-scoped daily withdrawal ceiling and the free-withdrawal
                  allowance are <strong>not shown because nothing holds them</strong>: no limit is
                  kept per KYC state, so a figure here would be invented.
                </div>
              </div>
            </>
          )}
        </Panel>

        <Panel title="Recent provider errors">
          {errorsLoading && errors.length === 0 ? (
            <div className="skeleton" style={{ height: '160px' }}>
              &nbsp;
            </div>
          ) : (
            <>
              <DataTable
                caption="Provider error codes with their 30-day deposit count, retry guidance and meaning"
                columns={errorColumns}
                rows={errors}
                rowKey={(e) => e.code}
                empty={
                  <EmptyState
                    icon={errorsFailed ? 'warning-circle' : 'check-circle'}
                    title={
                      errorsFailed
                        ? 'The error codes could not be read'
                        : 'No provider error codes'
                    }
                  >
                    {/* THE REAL-ZERO BRANCH USED TO NAME TWO TABLES. It read
                        "payment.payment_error_code is empty … the deposit table
                        requires a code on every FAILED row", which told an
                        operator nothing they could use and told them the two
                        identifiers instead of the consequence. The consequence
                        is the whole point of the sentence and is kept: a
                        deposit recorded as failed always carries a provider's
                        code — `payment.deposit`'s `deposit_failure_has_code`
                        CHECK refuses the row otherwise — and this list is what
                        turns that code into a meaning and a retry decision, so
                        an empty list means every failure arrives unexplained.
                        The tables are `payment.payment_error_code` and
                        `payment.deposit`, named here and not on screen.

                        THE FAILURE BRANCH NAMED THE ENDPOINT TOO. It opened
                        "/api/payments/errors did not answer", and a path is of
                        no use to the person reading an empty runbook: they
                        cannot open it, they cannot hand it to anybody who will
                        act on it, and what they actually need from the sentence
                        — that this list never arrived at all, rather than
                        arriving empty — survives without it. The request is the
                        GET of `/api/payments/errors` recorded at the top of
                        this file. */}
                    {errorsFailed
                      ? 'The list of error codes could not be read, so this runbook is empty because nothing arrived — not because no code is registered. A failed deposit still has a code; this screen just cannot show it. Try again shortly.'
                      : 'No provider error code is registered. Nothing was filtered out — there is genuinely nothing stored yet. Every deposit recorded as failed carries a code from its provider, so until codes are registered here a failure arrives with nothing to explain it and no guidance on whether it is safe to retry.'}
                  </EmptyState>
                }
              />
              <div className="notice notice-quiet" style={{ marginTop: 'var(--space-4)' }}>
                <Icon name="info" />
                <div>
                  Counts are <strong>deposit failures over 30 days</strong>, not over 24 hours, and
                  they cover <strong>deposits only</strong>. Withdrawal
                  failures carry the same codes but are not counted here, so a payout-side outage
                  reads as zero on this screen.
                </div>
              </div>
            </>
          )}
        </Panel>
      </div>

      {/* The house pattern for a gap: name the section, give the reason. */}
      <Panel title="Not yet available">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>The two success figures do not mean the same thing.</strong>{' '}
              <strong>Success 30d</strong> is settled ÷ every deposit attempted, because a deposit
              resolves in seconds and an unsettled one is a failure in progress.{' '}
              <strong>Payouts 30d</strong> is released ÷ every payout DECIDED, and the requests
              still waiting are named beside it rather than counted in. A payout can sit at a
              checker for days, so folding those in would make the figure fall whenever this desk
              is behind and rise when somebody works the queue — the backlog wearing a payment
              provider's name. A rail with nothing decided yet says so; it does not report nought
              per cent, which is a different fact.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Fee cost 30d</strong> — the deposit fee and{' '}
              the withdrawal fee are what the operator actually paid, and neither
              is in the response. The Fee column above is the contracted rate card, not incurred
              cost; multiplying one by settled volume would produce a plausible number that no
              ledger agrees with.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>24-hour windows</strong> — every measured figure on this screen is 30 days,
              because that is the interval both counts use. Headings say 30d rather than relabel
              the same number.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Rail latency and health history</strong> — no per-rail time series exists, so
              a degraded rail can be listed but its latency and the moment it degraded cannot.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Who changed a rail, and why</strong> — every change made on this screen (a
              rail&rsquo;s status, who pays its payout fee, the brands that offer it and its limits)
              is on the audit trail with the admin who made it and the reason they gave, but no
              screen here lists a rail&rsquo;s history: the trail is read under Access control and
              in an audit export.
            </div>
          </div>
        </div>
      </Panel>

      {/* ── Providers drill-down ───────────────────────────────────────────── */}
      <Drawer
        open={providersOpen}
        onClose={() => setProvidersOpen(false)}
        title="Payment providers"
        subtitle={
          railsFailed
            ? 'The rail list could not be read, so there is nothing to group'
            : `${providers.length} providers across ${rails.length} rails · grouped from the rails`
        }
      >
        <div className="stack">
          <DataTable
            caption="Payment providers, with the rails each one serves and the current state of those rails"
            columns={providerColumns}
            rows={providers}
            rowKey={(p) => p.provider}
            empty={
              <EmptyState icon="buildings" title="No providers">
                {railsFailed
                  ? 'Providers are grouped out of the rails, and the rail list could not be read. Nothing here means no provider is configured.'
                  : 'Providers are grouped out of the rails, and no rail is configured.'}
              </EmptyState>
            }
          />
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              This is a grouping, not a provider register. A provider is stored as a plain name on
              the rail, and there is no provider record anywhere, so{' '}
              <strong>settlement window, contracted fee and provider health have no source</strong>{' '}
              and are omitted rather than guessed. The rail states shown are operator-set states,
              which the module's first rule says are independent of provider health.
            </div>
          </div>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              One rail served by more than one provider, with a failover order set per rail, is
              not possible yet. The platform keeps exactly one provider per rail and no order, so
              failover cannot be displayed or edited here.
            </div>
          </div>
        </div>
      </Drawer>

      {/* ── Enable / Disable ───────────────────────────────────────────────── */}
      {/*
       * WHY THIS ONE CANNOT BE DISMISSED, AND WHY THAT IS NOT `!togglingBusy`.
       *
       * The tempting guard is `dismissible={!togglingBusy}`, and it protects
       * the wrong moment. It blocks Escape while the write is in flight, which
       * is the one moment the decision has already been made and the operator
       * has nothing left to lose. The moment that matters is before the press:
       * somebody who has picked a destination and typed out why players cannot
       * cash out, mid-outage, with a provider on the phone, loses the reason
       * and the decision to one stray key and is told nothing about it. So the
       * guard is `false` outright — this closes on Cancel, which is a choice a
       * person made, and not on a brush of the keyboard or a click beside the
       * box.
       */}
      <Dialog
        open={toggling !== null}
        onClose={() => setToggling(null)}
        dismissible={false}
        title={
          toggling === null
            ? ''
            : toggling.status === 'DISABLED'
              ? `Enable ${toggling.name}`
              : `Disable ${toggling.name}?`
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setToggling(null)} disabled={togglingBusy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void applyStatus()} disabled={togglingBusy}>
              Apply it
            </Button>
          </>
        }
      >
        {toggling && (
          <div className="stack">
            {/* The design hard-codes "GCash carries 58% of deposits" for one rail.
                The share is computable for every rail from settled 30-day value,
                so it is measured here instead of asserted for one name. */}
            {toggleShareBp !== null && toggleShareBp > 0 && (
              <p className="text-muted" style={{ margin: 0 }}>
                {toggling.name} carried {formatRate(toggleShareBp)} of settled deposit value over
                the last 30 days ({formatPesosCompact(BigInt(toggling.deposit_value))}).
              </p>
            )}
            <Field label="Move it to">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={statusTo}
                  onChange={(e) => setStatusTo(e.target.value as RailStatus)}
                >
                  <option value="LIVE">Live — offered to players normally</option>
                  <option value="DEGRADED">
                    Degraded — still offered, shown to players as unwell
                  </option>
                  <option value="DISABLED">Disabled — not offered to players at all</option>
                </Select>
              )}
            </Field>
            {/*
              * WHY DEGRADED IS AN OPERATOR'S CHOICE HERE.
              *
              * This screen used to say "Degraded is set by monitoring, Disabled
              * by an admin". Nothing in this platform writes this column — no
              * worker, no health check, no route — so the one degraded rail came
              * from the seed and monitoring could never have produced another.
              * With no monitoring behind it, a state the player-facing list
              * reads and nobody can set is a warning the platform can show and
              * nobody can raise.
              */}
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                <strong>All three are yours to set.</strong> Nothing else writes this — there is no
                monitoring behind these states, so a rail is degraded exactly when somebody says
                so. <strong>Degraded</strong> keeps the rail on offer and tells players it is
                unwell; <strong>Disabled</strong> takes it off the list entirely.
              </div>
            </div>
            <Field
              label="Why"
              error={statusReasonError}
              hint="At least 10 characters, kept on the rail. This is what the next operator reads when they ask why players could not cash out."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Provider reported an outage at 14:10; reopening when they confirm."
                />
              )}
            </Field>
            {/*
             * WHAT THE PRESS DOES, SAID LAST AND BESIDE THE BUTTON.
             *
             * Three things were decided here and each one is worth writing
             * down, because the obvious version of this notice gets all three
             * wrong.
             *
             * IT IS WARN AND NOT DANGER. The tone contract beside the two
             * classes in components.css reserves danger for the irreversible
             * and for anything that moves money or a reported figure. Taking a
             * rail off moves no money — it stops new money moving, and what is
             * already on the road is left alone — and the way back is this same
             * button, in the same number of presses. Even disabling the last
             * rail standing, which is the heaviest thing on this screen, is
             * undone by reopening this box and choosing Live. The one genuinely
             * irreversible move in this module is bringing back a card or
             * crypto rail, which the licence bars and the route refuses; that
             * rail never reaches this dialog, because the table prints
             * "Cannot be enabled" where its button would be. So danger here
             * would be danger on every rail change in the console, and a tone
             * that fires on everything tells an operator nothing.
             *
             * IT IS KEYED ON THE DESTINATION, NOT ON WHERE THE RAIL IS NOW.
             * This prose used to sit at the top of the dialog and switch on the
             * rail's CURRENT status, which made it lie on the case nobody
             * tested: moving a Live rail to Degraded is not "new deposits and
             * payouts stop", it is the rail staying on offer with a warning on
             * it — the quiet notice above says exactly that, two fields higher.
             * The consequence belongs to the dropdown, so it is read from the
             * dropdown, and it sits BELOW the dropdown so it changes where the
             * operator is already looking rather than above their eyeline.
             *
             * WHAT WOULD BE LEFT. Disabling the last rail a player can reach is
             * a legitimate thing to do in an outage and a terrible thing to do
             * by accident, and the difference is whether the person pressing it
             * knew. Counted from the list on screen, and said first, because on
             * the one occasion it reads zero it is the only sentence in this
             * box that matters.
             *
             * The rest is what an operator would otherwise find out afterwards:
             * nothing is sent to anybody. No player is emailed when a rail goes
             * off, comes back or is marked unwell — the rail simply stops being
             * offered, or starts again, and a degraded one carries one line in
             * the player's rail list. And nothing checks the provider before
             * the rail reopens, because no integration test or provider health
             * exists anywhere on this platform to check, so the first player to
             * pick a rail that is still broken is the test.
             */}
            <div className="notice notice-warn">
              <Icon name="warning" />
              <div>
                {statusTo === toggling.status ? (
                  <>
                    <strong>
                      This rail is already{' '}
                      {STATUS_LABEL[toggling.status]?.toLowerCase() ?? 'in that state'}.
                    </strong>{' '}
                    The press is refused and nothing changes — pick another destination, or cancel.
                  </>
                ) : statusTo === 'DISABLED' ? (
                  <>
                    {liveAfterDisabling === 0 ? (
                      <>
                        <strong>This is the last rail players can use.</strong> Disabling it leaves
                        no way to deposit or withdraw at all.
                      </>
                    ) : (
                      <>
                        <strong>
                          {formatCount(liveAfterDisabling)}{' '}
                          {liveAfterDisabling === 1 ? 'rail' : 'rails'} would be left
                        </strong>{' '}
                        for players.
                      </>
                    )}{' '}
                    New deposits and payouts on this rail stop within 30 seconds. In-flight
                    transactions are left to settle — a deposit or payout already raised keeps this
                    rail and still goes through. No player is emailed; the rail stops being offered,
                    and putting it back is this same button.
                  </>
                ) : statusTo === 'DEGRADED' ? (
                  <>
                    <strong>The rail stays on offer</strong> and keeps taking deposits and payouts.
                    Players who pick it are shown one line — that it is degraded and may be slower
                    than usual — and nothing else is sent to them. Clearing it is this same button.
                  </>
                ) : (
                  <>
                    Enabling a disabled rail applies immediately — players see the rail return
                    within 30 seconds. <strong>Nothing tests the provider first</strong>, so if the
                    rail is still unwell the next player to pick it is the one who finds out.
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* ── Who pays the payout fee ─────────────────────────────────────── */}
      <Dialog
        open={movingFee !== null}
        onClose={() => setMovingFee(null)}
        title={movingFee ? `Payout fee on ${movingFee.name}` : ''}
        actions={
          <>
            <Button variant="ghost" onClick={() => setMovingFee(null)} disabled={movingFeeBusy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void moveFee()} disabled={movingFeeBusy}>
              Apply it
            </Button>
          </>
        }
      >
        {movingFee === null ? null : (
          <div className="stack">
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                This rail charges{' '}
                <strong>
                  {formatRate(Number(movingFee.fee_basis_points))}
                  {movingFee.fee_flat !== '0'
                    ? ` plus ${formatPesos(BigInt(movingFee.fee_flat))}`
                    : ''}
                </strong>{' '}
                on a payout. Whoever bears it, the player&rsquo;s balance falls by the amount they
                asked for — what changes is how much <strong>arrives</strong>. On a ₱1,000 payout
                charged {formatRate(Number(movingFee.fee_basis_points))}, the player receives the
                full ₱1,000 while the operator bears it, and less once they do.
                <br />
                <strong>Deposits are not affected.</strong> A deposit fee is the provider&rsquo;s
                own model and no setting here changes it.
              </div>
            </div>
            <Field label="Who pays it">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={feeBearer}
                  onChange={(e) =>
                    setFeeBearer(e.target.value === 'PLAYER' ? 'PLAYER' : 'OPERATOR')
                  }
                >
                  <option value="OPERATOR">The operator — the player receives the full amount</option>
                  <option value="PLAYER">The player — deducted from what they receive</option>
                </Select>
              )}
            </Field>
            <Field
              label="Why"
              error={feeReasonError}
              hint="At least 10 characters, and kept on the rail. This is what the next operator reads when a player asks why their payout arrived short."
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={feeReason}
                  onChange={(e) => setFeeReason(e.target.value)}
                  placeholder="Rail costs moved to players from October, per the commercial review."
                />
              )}
            </Field>
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                It applies to payouts requested <strong>from now on</strong>. A request already in
                the queue keeps the fee it was raised with, because that is the figure the player
                was shown when they asked.
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* ── The brands that offer a rail, and its limits ──────────────────── */}
      <RailBrandsDialog
        rail={brandsFor}
        payoutDriver={payoutDriver}
        onClose={() => setBrandsFor(null)}
        onChanged={() => setReload((n: number) => n + 1)}
      />
      <RailLimitsDialog
        rail={limitsFor}
        onClose={() => setLimitsFor(null)}
        onChanged={() => setReload((n: number) => n + 1)}
      />

      {/* ── Add rail ───────────────────────────────────────────────────────── */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add payment rail"
        actions={
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            Close
          </Button>
        }
      >
        <div className="stack">
          <p style={{ margin: 0 }}>
            A new rail starts Disabled until the provider integration test passes and limits are
            confirmed by Finance.
          </p>
          {/* No form is rendered rather than a form that cannot submit: fields an
              operator can fill and never send are worse than an honest refusal. */}
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              <strong>Adding a rail is not built.</strong> Nothing on the platform can create
              one, and the two gates named in the rule above have nothing behind them either — no
              integration-test result and no Finance sign-off on limits is recorded anywhere. A form
              here would collect a name, a type, a provider and two limits and throw all five
              away.
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
